import { useState, useEffect } from "react";
import type { AssignmentRule, AssignmentRuleCondition, AssignmentRuleAction } from "@/mocks/assignmentData";
import { conditionFields, conditionOperators, conditionValueOptions } from "@/mocks/assignmentData";
import { fetchApi } from "@/services/apiService";

type Props = {
  rule: AssignmentRule | null;
  onClose: () => void;
  onSave: (data: Omit<AssignmentRule, "id">, id?: string) => void;
  existingRules: AssignmentRule[];
  liveAgents?: { id: string; name: string; role?: string }[];
};

export default function RuleModal({ rule, onClose, onSave, existingRules, liveAgents }: Props) {
  const isEditing = !!rule;

  const [name, setName] = useState(rule?.name ?? "");
  const [field, setField] = useState(rule?.condition.field ?? "Source");
  const [operator, setOperator] = useState(rule?.condition.operator ?? "is");
  const [value, setValue] = useState(rule?.condition.value ?? "");
  const [actionType, setActionType] = useState<"specific" | "round_robin">(rule?.action.type ?? "specific");
  const [agentId, setAgentId] = useState(rule?.action.agentId ?? "");
  const [priority, setPriority] = useState(rule?.priority ?? existingRules.length + 1);
  const [active, setActive] = useState(rule?.active ?? true);
  const [agentsList, setAgentsList] = useState<{ id: string; name: string; role?: string }[]>(liveAgents || []);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const valueOptions = conditionValueOptions[field] ?? [];

  useEffect(() => {
    if (liveAgents && liveAgents.length > 0) {
      setAgentsList(liveAgents);
    } else {
      fetchApi<any>("/auth/company-user/all", undefined, null)
        .then((res) => {
          const userList = Array.isArray(res)
            ? res
            : Array.isArray(res?.items)
            ? res.items
            : Array.isArray(res?.data?.items)
            ? res.data.items
            : Array.isArray(res?.data)
            ? res.data
            : [];
          if (userList.length > 0) {
            setAgentsList(
              userList.map((u: any, idx: number) => ({
                id: String(u.id || idx + 1),
                name: u.name || u.fullName || u.username || `Agent ${idx + 1}`,
                role: u.role || u.position || "Sales Agent",
              }))
            );
          }
        })
        .catch(() => {});
    }
  }, [liveAgents]);

  useEffect(() => {
    if (!rule && valueOptions.length > 0 && !value) {
      setValue(valueOptions[0]);
    }
  }, [field, valueOptions, value, rule]);

  useEffect(() => {
    if (rule) return;
    const maxPriority = existingRules.reduce((max, r) => Math.max(max, r.priority), 0);
    setPriority(maxPriority + 1);
  }, [existingRules, rule]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "Rule name is required";
    if (!field) errs.field = "Condition field is required";
    if (!value) errs.value = "Condition value is required";
    if (actionType === "specific" && !agentId) errs.agentId = "Select an agent";
    if (!priority || priority < 1 || priority > 10) errs.priority = "Priority must be 1-10";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    const condition: AssignmentRuleCondition = { field, operator, value };
    const selectedAgent = agentsList.find((a) => a.id === agentId || a.name === agentId);
    const action: AssignmentRuleAction =
      actionType === "round_robin"
        ? { type: "round_robin" }
        : {
            type: "specific",
            agentId,
            agentName: selectedAgent?.name || agentId || "Sales Agent",
          };

    onSave({ name: name.trim(), active, condition, action, priority, isFallback: false }, rule?.id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 animate-fade-in">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl border border-gray-200 animate-slide-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between pb-3 mb-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">
            {isEditing ? "Edit Assignment Rule" : "Create Assignment Rule"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Rule Name */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Rule Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. IndiaMart Leads → Sales Team"
            className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
          />
          {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
        </div>

        {/* IF Condition */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">IF Condition</label>
          <div className="flex items-center gap-2">
            <select
              value={field}
              onChange={(e) => { setField(e.target.value); setValue(""); }}
              className="flex-1 px-3 py-2 border border-gray-200 bg-white rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              {conditionFields.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
            <select
              value={operator}
              onChange={(e) => setOperator(e.target.value)}
              className="w-20 px-2 py-2 border border-gray-200 bg-white rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              {conditionOperators.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-200 bg-white rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              <option value="">Select...</option>
              {valueOptions.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
          {errors.field && <p className="text-xs text-red-500 mt-1">{errors.field}</p>}
          {errors.value && <p className="text-xs text-red-500 mt-1">{errors.value}</p>}
        </div>

        {/* THEN Action */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">THEN Action</label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="actionType"
                checked={actionType === "specific"}
                onChange={() => setActionType("specific")}
                className="text-indigo-600 focus:ring-indigo-500 w-4 h-4"
              />
              <span className="text-xs font-medium text-gray-700">Assign to specific agent</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="actionType"
                checked={actionType === "round_robin"}
                onChange={() => setActionType("round_robin")}
                className="text-indigo-600 focus:ring-indigo-500 w-4 h-4"
              />
              <span className="text-xs font-medium text-gray-700">Use round robin</span>
            </label>
          </div>
          {actionType === "specific" && (
            <select
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className="mt-2.5 w-full px-3.5 py-2 border border-gray-200 bg-white rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              <option value="">Select agent...</option>
              {agentsList.map((a) => (
                <option key={a.id} value={a.id}>{a.name} ({a.role || 'Sales Agent'})</option>
              ))}
            </select>
          )}
          {errors.agentId && <p className="text-xs text-red-500 mt-1">{errors.agentId}</p>}
        </div>

        {/* Priority & Status row */}
        <div className="flex items-center gap-4 mb-5">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Priority Order</label>
            <input
              type="number"
              min={1}
              max={10}
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
              className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
            {errors.priority && <p className="text-xs text-red-500 mt-1">{errors.priority}</p>}
          </div>
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Status</label>
            <label className="flex items-center gap-2.5 mt-1.5 cursor-pointer">
              <button
                type="button"
                role="switch"
                aria-checked={active}
                onClick={() => setActive(!active)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
                  active ? "bg-indigo-600" : "bg-gray-200"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    active ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
              <span className="text-xs font-semibold text-gray-700">{active ? "Active" : "Inactive"}</span>
            </label>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold cursor-pointer shadow-sm"
          >
            Save Rule
          </button>
        </div>
      </div>
    </div>
  );
}